import React, { useMemo, useReducer } from 'react'

import classnames from 'classnames'
import { t, Trans } from 'src/translations'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import {
  DeliveryInfoInput,
  DiningOptionBehavior,
  I18n,
  UpcomingSchedules,
  useValidateDeliveryDistanceQuery
} from 'src/apollo/onlineOrdering'
import {
  formatTimeString,
  getDayFromDate,
  getTakingOrdersTillString,
  intervalContainsNow,
  isTodaysSchedule
} from 'src/lib/js/schedule'
import Image from 'src/shared/components/common/Image'
import ContextualLoadingSpinner from 'src/shared/components/common/loading_spinner/LoadingSpinner'
import Popover from 'src/shared/components/common/popover/Popover'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { formatRestaurantAddress } from 'shared/js/locationUtils'

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import ToastProduct from 'public/components/online_ordering/ToastProduct'
import { ScheduleType } from 'public/components/online_ordering/types'
import LocationCta, {
  CtaDestination
} from 'public/components/pages/location_selection_page/LocationCta'
import {
  CaretDown,
  DeliveryIcon,
  PickupIcon
} from 'public/components/pages/location_selection_page/LocationSelectionIcons'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

export type DistanceUnits = 'mi' | 'km'

type Props = {
  index: number
  location: {
    address1: string
    address2?: string | null
    city: string
    state: string
    country: string
    zipcode: string
    name?: string | null
    externalId: string
    shortUrl?: string | null
    distanceToCustomer: number
    distanceUnits: DistanceUnits
    phoneNumber?: string | null
    i18n?: I18n
    schedule?:
      | {
          days: string[]
          intervals?:
            | {
                endTime: string
                startTime: string
                overnight?: boolean | null
              }[]
            | null
            | undefined
        }[]
      | null
      | undefined
  }
  schedule?: ScheduleType
  onSelect: (guid?: string) => void
  selected: boolean
  deliveryInfo?: DeliveryInfoInput | null
  diningOptionBehavior: DiningOptionBehavior
  skipDeliveryValidation?: boolean
}

export const getAvailableBehaviors = (schedule: ScheduleType) => {
  const behaviors =
    schedule.schedule.upcomingSchedules
      .filter((schedule) => schedule.dailySchedules.length)
      .map((behavior) => behavior.behavior) || []

  if (schedule?.schedule.asapAvailableForDelivery) {
    behaviors.push(DiningOptionBehavior.Delivery)
  }

  if (schedule?.schedule.asapAvailableForTakeout) {
    behaviors.push(DiningOptionBehavior.TakeOut)
  }

  const behaviorSet = new Set([...behaviors])
  const deliveryServiceAvailability =
    schedule?.deliveryServiceAvailability?.available

  // If a location uses 3PD, their delivery schedule is their takeout schedule --
  // if they have takeout availability we can assume they have delivery availability as well
  if (
    deliveryServiceAvailability &&
    behaviorSet.has(DiningOptionBehavior.TakeOut)
  ) {
    behaviorSet.add(DiningOptionBehavior.Delivery)
  }

  return behaviorSet
}

const convertUnit = (unit: DistanceUnits) =>
  unit === 'km' ? 'kilometer' : 'mile'
const formatDistance = (distance: number, units: DistanceUnits, i18n?: I18n) =>
  new Intl.NumberFormat(i18n?.locale || 'en-US', {
    style: 'unit',
    unit: convertUnit(units),
    unitDisplay: 'long',
    maximumFractionDigits: 1
  }).format(distance)

const getOptionBadge = (option: DiningOptionBehavior, color?: string) => {
  switch (option) {
    case DiningOptionBehavior.Delivery:
      return (
        <div key='delivery' className='optionBadge'>
          <Trans
            i18nKey='location-option.delivery'
            components={{ DeliveryIcon: <DeliveryIcon color={color} /> }}
          />
        </div>
      )
    case DiningOptionBehavior.TakeOut:
      return (
        <div key='pickup' className='optionBadge'>
          <Trans
            i18nKey='location-option.pickup'
            components={{ PickupIcon: <PickupIcon color={color} /> }}
          />
        </div>
      )
    default:
      return null
  }
}

const LocationOption = (props: Props, ref: React.RefObject<HTMLDivElement>) => {
  const { restaurant, toastProduct, host } = useRestaurant()
  const { orderPathPattern } = useRestaurantRoutes()
  const getColorFromTheme = useThemeColorScheme()
  const tracker = useTracker()
  const {
    selected,
    onSelect,
    location: loc,
    deliveryInfo,
    diningOptionBehavior
  } = props
  const [scheduleOpen, toggleSchedule] = useReducer((state) => !state, false)
  const iconColor = getColorFromTheme(
    (theme) => theme.colorScheme.icon.default,
    toastProduct === ToastProduct.Sites
      ? restaurant.meta.textColor
      : restaurant.config.ooConfig?.colors?.primaryText ?? undefined
  )

  const isDelivery = diningOptionBehavior === DiningOptionBehavior.Delivery

  const STREET_NUMBER_REGEX = /^\d/

  const isCompleteDeliveryInfo = (
    deliveryInfo: DeliveryInfoInput | null | undefined
  ): boolean => {
    if (!deliveryInfo?.address1) return false
    return STREET_NUMBER_REGEX.test(deliveryInfo.address1.trim())
  }

  const { data: validDelivery, loading } = useValidateDeliveryDistanceQuery({
    variables: {
      input: {
        deliveryInfo: deliveryInfo || ({} as DeliveryInfoInput),
        restaurantGuid: loc.externalId
      }
    },
    ssr: false,
    skip:
      !deliveryInfo ||
      !isDelivery ||
      !!props.skipDeliveryValidation ||
      !isCompleteDeliveryInfo(deliveryInfo)
  })

  const availableBehaviors = useMemo(
    () =>
      props.schedule
        ? getAvailableBehaviors(props.schedule)
        : new Set<DiningOptionBehavior>(),
    [props.schedule]
  )
  const hasAnyAvailability = !!availableBehaviors?.size
  const hasBehaviorAvailability = useMemo(
    () => availableBehaviors?.has(diningOptionBehavior),
    [availableBehaviors, diningOptionBehavior]
  )
  const isLocationOpen = useMemo(
    () =>
      (isDelivery
        ? props.schedule?.schedule.asapAvailableForDelivery
        : props.schedule?.schedule.asapAvailableForTakeout) ?? false,
    [isDelivery, props.schedule]
  )
  const timeZoneId = useMemo(
    () =>
      props.schedule?.timeZoneId ||
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    [props.schedule]
  )

  const diningBehaviorSchedules = props.schedule?.schedule.upcomingSchedules
  const diningBehaviorSchedule: UpcomingSchedules | undefined =
    diningBehaviorSchedules?.filter(
      (schedule) => schedule.behavior == diningOptionBehavior
    )[0]

  if (isDelivery && loading && props.index === 0) {
    return props.index === 0 ? <ContextualLoadingSpinner /> : null
  }

  // If delivery is selected and an option for this restaurant and we're out of range, don't display
  if (
    isDelivery &&
    !props.skipDeliveryValidation &&
    (!availableBehaviors?.has(DiningOptionBehavior.Delivery) ||
      !validDelivery?.validateDeliveryLocationV2?.valid)
  ) {
    return null
  }

  return (
    <div
      ref={ref}
      data-testid='location-option'
      onClick={() => {
        onSelect(loc.externalId)
      }}
      className={classnames('location', { selected })}
    >
      <div className='nameDistance'>
        <h2 className='name'>{loc.name}</h2>
        {loc.distanceToCustomer >= 0 ? (
          <div className='distance' data-testid='distance-to-customer'>
            {t('location-option.away', {
              distance: formatDistance(
                loc.distanceToCustomer,
                loc.distanceUnits,
                loc.i18n
              )
            })}
          </div>
        ) : null}
      </div>
      <div className='locationInfo'>
        {loc.phoneNumber ? (
          <a
            className='phoneNumber'
            href={`tel:${loc.phoneNumber}`}
            aria-label={t('location-details.call-specific-location', {
              phone: parsePhoneNumber(
                loc.phoneNumber,
                (loc.country || 'US') as CountryCode
              )?.formatNational(),
              location: loc.name
            })}
            onClick={() =>
              tracker.track(TrackingEvents.PHONE_NUMBER_CLICKED, {
                context: 'location_selection',
                restaurant_guid: loc.externalId
              })
            }
          >
            {parsePhoneNumber(
              loc.phoneNumber,
              (loc.country || 'US') as CountryCode
            )?.formatNational()}
          </a>
        ) : null}
        <div className='address'>{formatRestaurantAddress(loc)}</div>
      </div>
      <div key='unavailable'>
        {!hasAnyAvailability || !hasBehaviorAvailability ? (
          <div className='unavailable'>
            <Image src='icons/warning-badge.svg' alt='' />
            {hasAnyAvailability
              ? t('location-option.option-is-not-available', {
                  context:
                    diningOptionBehavior === DiningOptionBehavior.Delivery
                      ? 'delivery'
                      : 'pickup'
                })
              : t(
                  'location-option.delivery-and-pickup-not-available-at-location'
                )}
          </div>
        ) : null}
      </div>
      <div key='storeHours' className='storeHours'>
        <button
          className='hoursToggle'
          onClick={toggleSchedule}
          aria-expanded={scheduleOpen}
        >
          <div className='openTag'>
            {getTakingOrdersTillString(
              isLocationOpen,
              diningBehaviorSchedule,
              timeZoneId
            )}
          </div>
          <CaretDown
            color={getColorFromTheme(
              (theme) => theme.colorScheme.icon.default,
              'black'
            )}
          />
        </button>
        <dl
          className={classnames('schedule', { expanded: scheduleOpen })}
          aria-hidden={!scheduleOpen}
        >
          {diningBehaviorSchedule?.dailySchedules.map((dailySchedule) => {
            const isToday = isTodaysSchedule(dailySchedule, timeZoneId)
            return (
              <div key={dailySchedule.date} className='dayRow'>
                <dt className={isLocationOpen && isToday ? 'bold' : ''}>
                  {getDayFromDate(dailySchedule.date)}
                </dt>
                <dd className='hoursAndTooltip'>
                  <span className='hoursWrapper'>
                    {dailySchedule.servicePeriods.length
                      ? dailySchedule.servicePeriods.map((servicePeriod) => {
                          const isCurrentHours =
                            isLocationOpen &&
                            isToday &&
                            intervalContainsNow(
                              servicePeriod.startTime,
                              servicePeriod.endTime,
                              timeZoneId
                            )
                          return (
                            <div
                              className={classnames(
                                'scheduleHours',
                                isCurrentHours ? 'bold' : ''
                              )}
                              key={formatTimeString(
                                servicePeriod.startTime,
                                true
                              )}
                            >
                              {formatTimeString(servicePeriod.startTime, true)}{' '}
                              - {formatTimeString(servicePeriod.endTime, true)}
                            </div>
                          )
                        })
                      : t('location-option.closed')}
                  </span>
                  {dailySchedule?.overrideDescription ? (
                    <Popover
                      contextKey='scheduleOverridePopover'
                      target={({ open }) => (
                        <button
                          className='infoIcon'
                          onClick={open}
                          aria-label={t(
                            'location-option.show-schedule-changes'
                          )}
                        >
                          <Image src='icons/info.svg' alt='' />
                        </button>
                      )}
                    >
                      <div className='tooltipContent'>
                        {dailySchedule?.overrideDescription}
                      </div>
                    </Popover>
                  ) : null}
                </dd>
              </div>
            )
          })}
        </dl>
      </div>
      <div className='badges'>
        {Array.from(availableBehaviors)
          .sort()
          .map((behavior) => getOptionBadge(behavior, iconColor))}
      </div>
      {loc.shortUrl && host && (
        <div className='location-cta-container'>
          <LocationCta
            orderPathPattern={orderPathPattern}
            shortUrl={loc.shortUrl}
            hostName={host}
            rxName={loc.name}
            diningOptionBehavior={diningOptionBehavior}
            ctaDestination={
              hasAnyAvailability || restaurant.config.isOnlineOrderingOnly
                ? CtaDestination.OrderPage
                : CtaDestination.MenuPage
            }
          />
        </div>
      )}
    </div>
  )
}

export default React.forwardRef(LocationOption)
